import { Flex, Skeleton } from '@buffer-mono/popcorn'

import { type FragmentType, getFragmentData } from '~publish/gql'
import { PostCardProvider, PostCard_Post } from '~publish/components/PostCard'
import { PostCardHeader } from '~publish/components/PostCardHeader'
import { PostCardFooter } from '~publish/components/PostCardFooter'
import { PostCardTags } from '~publish/components/PostCardTags'
import { PostCardAddons } from '~publish/components/PostCardAddons'
import { PostCardContent } from '~publish/components/PostCardContent'
import { PostMediaBentoBoxPreview } from '~publish/components/PostCard/PostMedia'
import { PostCardAttachments } from '~publish/components/PostCard/PostCardAttachments'
import { PostCardErrorNotice } from '~publish/components/PostCard/PostCardErrorNotice'
import {
  getAnalyzeUrl,
  getEngageUrl,
} from '~publish/components/PostCard/helpers'
import {
  MetricsRefresh,
  MoreAnalyticsIconButton,
  PostMetricsScroller,
  PostMetricsWrapper,
} from '~publish/components/PostDetails/PostMetrics'
import { PostCardContent_Annotation } from '~publish/components/PostCardContent/PostCardContent'
import {
  useMetadataTitle,
  usePostData,
} from '~publish/components/PostCard/PostCardContext'

import { CommentsPostCard_Post } from '../fragments/CommentsPostCard_Post'
import { CommentsPostCardHeader } from './CommentsPostCardHeader'
import styles from './CommentsPostCard.module.css'
import { usePostDetailsDialog } from '~publish/components/PostDialogs'

const TagsAndAddons = (): JSX.Element => (
  <Flex direction="column" gap="xs" className={styles.tagsAddonsContainer}>
    <PostCardAddons />
    <PostCardTags maxVisibleTags={4} />
  </Flex>
)

const Metrics = (): JSX.Element | null => {
  const { metrics: metricsData, channel } = usePostData()
  const metrics = metricsData ?? []

  const engageUrl = getEngageUrl(channel)
  const analyzeUrl = getAnalyzeUrl(channel)

  const shouldShowMoreAnalyticsCTA =
    channel.service === 'linkedin' ? channel.type === 'page' : true

  return (
    <PostMetricsWrapper className={styles.metricsWrapper}>
      <PostMetricsScroller metrics={metrics} engageUrl={engageUrl} />
      {shouldShowMoreAnalyticsCTA && analyzeUrl && (
        <MoreAnalyticsIconButton analyzeUrl={analyzeUrl} />
      )}
      <MetricsRefresh />
    </PostMetricsWrapper>
  )
}

type CommentsPostCardProps = {
  post?: FragmentType<typeof PostCard_Post>
  partialData: FragmentType<typeof CommentsPostCard_Post>
}

export const CommentsPostCard = ({
  post,
  partialData,
}: CommentsPostCardProps): JSX.Element => {
  const postData = getFragmentData(PostCard_Post, post)
  const partialPostData = getFragmentData(CommentsPostCard_Post, partialData)
  const { openDetailsDialog } = usePostDetailsDialog({
    postId: postData?.id ?? partialPostData.id,
  })
  const title = useMetadataTitle(postData?.metadata ?? partialPostData.metadata)
  const type = postData?.metadata?.type ?? partialPostData.metadata?.type
  const annotations =
    getFragmentData(
      PostCardContent_Annotation,
      postData?.metadata?.annotations,
    ) ?? []

  const openExpandedDetailsDialog = (): void => {
    openDetailsDialog({
      cta: 'publish-comments-postCardText-seeMore-1',
      expandText: 'true',
    })
  }

  return (
    <PostCardProvider
      post={
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        post ?? (partialData as unknown as FragmentType<typeof PostCard_Post>)
      }
    >
      <article className={styles.cardContainer}>
        <PostCardErrorNotice clampErrorMessage />
        <CommentsPostCardHeader />
        <section className={styles.body}>
          <div className={styles.bodyWrapper}>
            <div className={styles.contentWrapper}>
              <PostCardHeader type={type} condensed />
              <PostCardContent
                title={title}
                text={postData?.text ?? partialPostData.text}
                annotations={annotations}
                onSeeMoreClick={openExpandedDetailsDialog}
              />
              <PostCardAttachments condensed />
            </div>
            <div>
              <PostMediaBentoBoxPreview
                assets={postData?.assets ?? partialPostData.assets}
                className={styles.bentoBox}
              />
            </div>
          </div>
          {postData ? (
            <TagsAndAddons />
          ) : (
            <div className={styles.tagsAddonsContainerSkeleton}>
              <Skeleton height={24} width={24} />
            </div>
          )}
        </section>
        {postData && <Metrics />}
        {postData ? (
          <PostCardFooter condensed />
        ) : (
          <PostCardFooter.Skeleton isSent={partialPostData.status === 'sent'} />
        )}
      </article>
    </PostCardProvider>
  )
}
